import { captureException } from "@sentry/core";
import { type ReactNode, useEffect, useState } from "react";
import { type ApiError, api } from "../api/ApiClient";
import { useAuth } from "./AuthContext";
import { type Customer, CustomerContext } from "./CustomerContext";
import { useTrackContext } from "../tracking/TrackingContext";

export default function CustomerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { isAuthenticated } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [result, setResult] = useState<Customer | null>(null);

  useEffect(() => {
    async function getCustomer() {
      setIsLoading(true);

      try {
        const customer = await api.get("customer");

        setResult(customer as Customer);
      } catch (error) {
        captureException(error);
        setError(error as ApiError);
      } finally {
        setIsLoading(false);
      }
    }

    if (!isAuthenticated) return;

    getCustomer();
  }, [isAuthenticated]);

  // customerId is the NAF customer guid; the membership number is not sent to GA.
  useTrackContext("customerGuid", result?.customerId);
  useTrackContext("hasMembership", result?.hasMembership?.toString());

  const context = {
    isLoggedIn: isAuthenticated,
    isLoading,
    result,
    error,
  };

  return (
    <CustomerContext.Provider value={context}>
      {children}
    </CustomerContext.Provider>
  );
}
